// Crosshair
// Features:
// - Renders a central UI crosshair (dot + ripple effect on click)
// - Displays interaction labels (current hit object)
// - Manages visibility based on UI/Pointer state

import { useEffect, useRef } from 'react'
import gsap from 'gsap'

import { useStore } from '../../store/useStore'

const CROSSHAIR_CONFIG = {
    size: 20,
    color: 'rgba(255, 255, 255, 0.35)',
    rippleBorder: '2px solid rgba(255, 255, 255, 0.35)',
    zIndex: 'var(--hint-z, 10000)',
}

const CONTAINER_STYLE = {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    zIndex: CROSSHAIR_CONFIG.zIndex,
}

const CENTER_STYLE = {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: CROSSHAIR_CONFIG.size,
    height: CROSSHAIR_CONFIG.size,
}

const DOT_STYLE = {
    position: 'absolute',
    inset: 0,
    borderRadius: '75%',
    background: CROSSHAIR_CONFIG.color,
    willChange: 'transform, opacity',
}

const RIPPLE_STYLE = {
    position: 'absolute',
    inset: 0,
    borderRadius: '75%',
    border: CROSSHAIR_CONFIG.rippleBorder,
    opacity: 0,
    willChange: 'transform, opacity',
}

const TEXT_STYLE = {
    position: 'absolute',
    left: '50%',
    top: 'calc(50% + 32px)',
    transform: 'translateX(-50%)',
    color: '#fff',
    fontFamily: "var(--hint-font, var(--loader-font, 'Luckiest Guy', cursive))",
    fontSize: 'var(--hint-fs, 28px)',
    lineHeight: 1.25,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap',
}

export default function Crosshair() {
    const crosshairOn = useStore((state) => state.crosshairOn)
    const uiOn = useStore((state) => state.uiOn)
    const hitName = useStore((state) => state.currentHitName)

    const dotRef = useRef(null)
    const rippleRef = useRef(null)

    // Click Animation
    useEffect(() => {
        const onMouseDown = (event) => {
            if (event.button !== 0) return
            if (!useStore.getState().crosshairOn) return

            // Animate Dot (Wobble)
            if (dotRef.current) {
                const tl = gsap.timeline({ defaults: { overwrite: 'auto' } })
                tl.to(dotRef.current, {
                    duration: 0.06,
                    scale: 0.75,
                    opacity: 0.7,
                    ease: 'power2.out',
                })
                    .to(dotRef.current, {
                        duration: 0.12,
                        scale: 1.15,
                        opacity: 0.9,
                        ease: 'power2.out',
                    })
                    .to(dotRef.current, {
                        duration: 0.12,
                        scale: 1.0,
                        opacity: 0.85,
                        ease: 'power2.out',
                    })
            }

            // Animate Ripple (Expand & Fade)
            if (rippleRef.current) {
                gsap.fromTo(
                    rippleRef.current,
                    { scale: 0.6, opacity: 0.45 },
                    {
                        scale: 2.9,
                        opacity: 0,
                        duration: 0.35,
                        ease: 'power2.out',
                        overwrite: 'auto',
                    }
                )
            }
        }

        window.addEventListener('mousedown', onMouseDown)
        return () => window.removeEventListener('mousedown', onMouseDown)
    }, [])

    if (!crosshairOn || !uiOn) return null

    return (
        <div style={CONTAINER_STYLE}>
            {/* Centered dot & ripple */}
            <div style={CENTER_STYLE}>
                <span ref={dotRef} style={DOT_STYLE} />
                <span ref={rippleRef} style={RIPPLE_STYLE} />
            </div>

            {/* Hit Label */}
            {hitName && <div style={TEXT_STYLE}>{hitName}</div>}
        </div>
    )
}
